import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { repositories } from '@/data'
import { useSessionStore } from '@/lib/session.store'
import { useSelfServeStore } from '@/stores/self-serve.store'
import { useLogout } from '@/modules/shared/hooks/use-logout'

/**
 * First-run screen for authenticated users who belong to no workspace.
 * Stores must live in an agency (the personal workspace was retired), so
 * submit creates a self-serve workspace, then the first store attached to
 * it, then continues into that store's onboarding (TikTok connect). Invited
 * users whose membership was never attached can still sign out or wait for
 * their admin instead.
 */
export function WelcomePage() {
  const navigate = useNavigate()
  const { logout, isLoggingOut } = useLogout()
  const email = useSessionStore((s) => s.user.email)
  const [name, setName] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Store name is required')
      return
    }
    if (trimmed.length > 255) {
      setError('Store name must be 255 characters or fewer')
      return
    }
    const cur = currency.trim().toUpperCase()
    if (cur.length !== 3) {
      setError('Currency must be a 3-letter code (e.g. USD)')
      return
    }
    setLoading(true)
    try {
      const org = await repositories.auth.createOrganization(trimmed)
      // Self-serve workspaces are dashboard-gated until SCC activates them.
      useSelfServeStore.getState().mark(org.id)
      const created = await repositories.auth.createStore({
        name: trimmed,
        currency: cur,
        agencyId: Number(org.id),
      })
      // The server assigns client slugs per workspace; prefer its value over
      // the locally derived one so the onboarding URL always resolves.
      const clients = await repositories.auth.getClients(org.id)
      const clientSlug = clients.find((c) => c.id === created.id)?.slug ?? created.slug
      await navigate({
        to: '/$orgSlug/onboarding/$clientSlug',
        params: { orgSlug: org.slug, clientSlug },
        search: { connect: true },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create store')
      setLoading(false)
    }
  }

  return (
    <div className="auth-frame" style={{ display: 'grid', placeItems: 'center' }}>
      <section className="auth-form-panel" style={{ maxWidth: 480 }}>
        <div className="auth-form-wrap">
          <div className="auth-eyebrow">Welcome</div>
          <h1 className="auth-h">
            Let&apos;s set up your <em>store</em>.
          </h1>
          <p className="auth-sub">
            You&apos;re signed in as {email}, but this account isn&apos;t a member of any
            workspace yet. Create your first store to connect TikTok Shop — or, if you were
            invited to an existing workspace, ask your admin to attach your account and
            reload this page.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label className="auth-label" htmlFor="welcome-store-name">
                Store name
              </label>
              <div className={`auth-input-wrap${name ? ' filled' : ''}`}>
                <input
                  id="welcome-store-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Northstar Beauty"
                  maxLength={255}
                  autoFocus
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="welcome-store-currency">
                Currency
              </label>
              <div className="auth-input-wrap">
                <input
                  id="welcome-store-currency"
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  maxLength={3}
                  style={{ width: 110 }}
                />
              </div>
            </div>

            {error && (
              <p className="auth-helper error" role="alert" style={{ marginBottom: 12 }}>
                {error}
              </p>
            )}

            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create store'}
            </button>
          </form>

          <div style={{ marginTop: 20 }}>
            <button
              type="button"
              className="scc-btn"
              onClick={() => void logout()}
              disabled={isLoggingOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
